// Live updates: one WebSocket per tab. Components subscribe to topics they're showing; when an event
// arrives, the matching cached queries are refreshed, so everyone sees changes within a second.
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export interface LiveEvent { topic: string; kind: string; data: Record<string, unknown> }
type Listener = (e: LiveEvent) => void;

interface LiveApi {
  connected: boolean;
  subscribe: (topics: string[]) => void;
  on: (listener: Listener) => () => void;
}

const LiveContext = createContext<LiveApi>({ connected: false, subscribe: () => {}, on: () => () => {} });

// Which cached queries each kind of event makes stale.
const INVALIDATES: Record<string, string[][]> = {
  'client.created': [['clients']],
  'client.updated': [['clients'], ['client']],
  'user.created': [['users']],
  'user.updated': [['users']],
  'invitation.created': [['invitations']],
  'project.changed': [['projects'], ['project'], ['spec-handoff'], ['ideas']],
  'product.changed': [['products'], ['projects'], ['project']],
  'round.changed': [['project-summary'], ['rounds'], ['round'], ['ideas'], ['questions'], ['comments'], ['review'], ['review-state'], ['review-comments']],
  'job.changed': [['project-summary'], ['rounds'], ['round'], ['job'], ['spec-handoff'], ['spec'], ['ideas'], ['kit'], ['kits'], ['brief']],
  'backup.changed': [['backups']],
  'export.changed': [['exports']],
  'kit.changed': [['kit'], ['kits'], ['rounds'], ['project-kit']],
  'idea.changed': [['project-summary'], ['ideas'], ['comments']],
  'spec.changed': [['spec'], ['spec-handoff'], ['questions']],
  'brief.changed': [['brief']],
  'ticket.changed': [['progress'], ['portfolio'], ['board'], ['ticket'], ['ticket-comments'], ['ticket-designs'], ['round-tickets'], ['comments'], ['project-summary']],
  'crm.changed': [['contacts'], ['activities'], ['tasks'], ['pipeline'], ['opportunities'], ['clients'], ['client'], ['crm-summary'], ['crm-reports'], ['client-research'], ['crm-emails'], ['unfiled']],
  'research.changed': [['client-research'], ['research-session']],
  'notification.created': [['notifications']],
  'notification.read': [['notifications']],
  'questions.changed': [['project-summary'], ['questions'], ['spec-handoff'], ['client-questions'], ['review-questions'], ['review-state']],
  'comment.changed': [['project-summary'], ['comments'], ['rounds'], ['round'], ['review'], ['review-state'], ['review-comments']],
  'review.changed': [['project-summary'], ['review'], ['review-state'], ['review-comments'], ['review-questions']],
};

export function LiveProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const ws = useRef<WebSocket | null>(null);
  const topics = useRef(new Set<string>());
  const listeners = useRef(new Set<Listener>());

  useEffect(() => {
    if (!enabled) return;
    let closed = false;
    let retry = 1000;
    let timer: number | undefined;
    const connect = () => {
      const socket = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`);
      ws.current = socket;
      socket.onopen = () => {
        setConnected(true);
        retry = 1000;
        if (topics.current.size) socket.send(JSON.stringify({ op: 'subscribe', topics: [...topics.current] }));
      };
      socket.onmessage = (msg) => {
        const evt = JSON.parse(msg.data) as LiveEvent;
        if (evt.kind === 'ping' || evt.kind === 'subscribed') return;
        for (const key of INVALIDATES[evt.kind] ?? []) qc.invalidateQueries({ queryKey: key });
        if (evt.kind.startsWith('audit')) qc.invalidateQueries({ queryKey: ['audit'] });
        listeners.current.forEach((l) => l(evt));
      };
      socket.onclose = () => {
        setConnected(false);
        if (!closed) timer = window.setTimeout(connect, retry = Math.min(retry * 2, 15000));
      };
    };
    connect();
    return () => { closed = true; window.clearTimeout(timer); ws.current?.close(); };
  }, [enabled, qc]);

  const api: LiveApi = {
    connected,
    subscribe: (list) => {
      const fresh = list.filter((t) => !topics.current.has(t));
      fresh.forEach((t) => topics.current.add(t));
      if (fresh.length && ws.current?.readyState === WebSocket.OPEN) ws.current.send(JSON.stringify({ op: 'subscribe', topics: fresh }));
    },
    on: (listener) => { listeners.current.add(listener); return () => { listeners.current.delete(listener); }; },
  };
  return <LiveContext.Provider value={api}>{children}</LiveContext.Provider>;
}

export const useLive = () => useContext(LiveContext);

/** Subscribe to topics while a component is on screen. */
export function useLiveTopics(...list: (string | null | undefined)[]) {
  const live = useLive();
  const key = list.filter(Boolean).join('|');
  useEffect(() => { if (key) live.subscribe(key.split('|')); }, [key, live.connected]); // eslint-disable-line react-hooks/exhaustive-deps
}
